import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  Availability,
  AvailabilityData,
  datePickerSelector,
  FlightType,
  ISearcherAvailabilityProvider,
  SearcherState
} from '@vueling-ng/vy-cdk-searcher';
import { catchError, map, Observable, of, switchMap, take } from 'rxjs';
import { AvailabilityRequest, AvailabilityResponse } from '../../../shared/searcher/models/availability.models';
import { AvailabilityLayerApi } from './availability.layer.api';
import { AVAILABILITY_PROVIDER_NAME } from './models/path-availability.enum';

@Injectable({
  providedIn: 'root'
})
export class AvailabilityService implements ISearcherAvailabilityProvider {
  public readonly providerName = AVAILABILITY_PROVIDER_NAME;

  private readonly _availabilityLayerApi = inject(AvailabilityLayerApi);
  private readonly _store = inject(Store<SearcherState>);

  public getAvailabilitiesAndPrices(origin: string, destination: string): Observable<AvailabilityData> {
    const date = new Date();
    return this._store.select(datePickerSelector.selectFlightType).pipe(
      take(1),
      switchMap((flightType) => {
        const request: AvailabilityRequest = this.buildAvailabilityRequest(origin, destination, flightType, date);
        return this._availabilityLayerApi
          .getLowPrices(request)
          .pipe(map((availabilities) => this.mapAvailabilities(availabilities)));
      }),
      catchError(() => of(this.getEmptyAvailabilityData()))
    );
  }

  private buildAvailabilityRequest(
    origin: string,
    destination: string,
    flightType: FlightType,
    date: Date
  ): AvailabilityRequest {
    return {
      originCode: origin,
      destinationCode: destination,
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      currencyCode: 'EUR',
      monthsRange: 17,
      flightType: flightType
    };
  }

  private mapAvailabilities(availabilityResponse: AvailabilityResponse[]): AvailabilityData {
    if (!availabilityResponse || availabilityResponse.length === 0) {
      return this.getEmptyAvailabilityData();
    }

    const items = availabilityResponse.map(
      (availability) =>
        ({
          date: new Date(availability.departureDate),
          price: {
            price: availability.price,
            currency: availability.currency,
            lowPrice: availability.isLowPrice ?? availability.isLeadingPrice,
            promoPrice: !availability.isInvalidPrice && availability.isLeadingPrice
          }
        }) as Availability
    );

    return { items, summary: { years: {} } };
  }

  private getEmptyAvailabilityData(): AvailabilityData {
    return { items: [], summary: { years: {} } };
  }
}
